import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Kalender,
  mag,
  type Afwezigheid,
  type BezetMap,
  type Engineer,
  type Feestdag,
  type Instellingen,
  type Project,
  type Recht,
  type Rol,
  type Taak,
  type Takenpakket,
  type Werkpakket,
  type Licentie,
  type Module,
  type EigenVeld,
} from '@ep/core';
import { api } from './api';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../ui/toast';

export interface Ik {
  uid: string | null;
  tid: string;
  email: string;
  naam: string;
  rol: Rol | null;
  eid?: string | null;
  imp?: string;
  organisatie: { id: string; naam: string; slug: string; plan: string; trialTot: string | null; graphConsent: boolean; maxEngineers: number; abonnement: string; huisstijl: { primair: string | null; accent: string | null; logoVersie: string | null } | null };
  /** SPEC §12: toegang en modules */
  licentie: Licentie;
  /** SPEC §16.3: datum waarop de omgeving wordt verwijderd (null = niet gepland) */
  verwijderOp?: string | null;
  /** SPEC §17: aantal organisaties waarnaar je kunt wisselen */
  organisaties?: number;
}

export type ProjectRij = Project & {
  locatie?: string | null;
  oplevering?: string | null;
  prognose?: string | null;
  pmTelefoon?: string | null;
  contactpersoon?: string | null;
  contactEmail?: string | null;
  teamsLink?: string | null;
  teamsExternLink?: string | null;
  omschrijving?: string | null;
  geoAdres?: string | null;
  geoNaam?: string | null;
  /** SPEC §11: geschreven minuten op het project zonder item (overleg op een agile werkpakket) */
  besteedLosMin?: number;
  /** SPEC §19 */
  velden?: Record<string, unknown>;
};
export type TaakRij = Taak & {
  soNr?: string | null;
  deelnemers?: string | null;
  omschrijving?: string | null;
  afwGeo?: string | null;
  sync: 'OK' | 'NODIG' | 'VERWIJDEREN' | 'FOUT';
  syncFout?: string | null;
  outlookEventId?: string | null;
  tmDatum?: string | null;
  tmOpmerking?: string | null;
  tmRestUren?: number | null;
  tmVoorkeur?: string | null;
  besteedMin?: number;
  velden?: Record<string, unknown>;
};
export type EngineerRij = Engineer & { geoAdres?: string | null; geoNaam?: string | null };
export type WerkpakketRij = Werkpakket & { omschrijving?: string | null; velden?: Record<string, unknown> };
export type TakenpakketRij = Takenpakket & { dagen: number; werkzaamheden?: string | null };
export interface TemplateRij {
  id: string;
  soort: 'WERKPAKKET' | 'TAKENPAKKET';
  ouderId: string | null;
  naam: string;
  volgorde: number;
  dagenTovLivegang: number | null;
  naVorige: boolean;
  expertise: string | null;
  uren: number;
  dagen: number;
  minNiveau: number;
  maxNiveau: number;
}
export interface GebruikerRij { id: string; naam: string; email: string; rol: Rol; engineerId: string | null; laatstGezien: string | null }

export interface SprintRij {
  id: string;
  werkpakketId: string;
  nummer: number;
  start: string;
  eind: string;
  doel: string | null;
  status: 'GEPLAND' | 'LOPEND' | 'AFGESLOTEN';
  leden: { engineerId: string; inzetPct: number }[];
}

export interface Data {
  vandaag: string;
  instellingen: Instellingen;
  engineers: EngineerRij[];
  expertises: { id: string; naam: string }[];
  projecten: ProjectRij[];
  werkpakketten: WerkpakketRij[];
  takenpakketten: TakenpakketRij[];
  sprints: SprintRij[];
  /** SPEC §11: geschreven minuten per engineer per dag (laatste 5 weken) */
  geschreven?: Record<string, Record<string, number>>;
  taken: TaakRij[];
  afwezigheden: (Afwezigheid & { adres?: string | null })[];
  feestdagen: Feestdag[];
  templates: TemplateRij[];
  bezet: Record<string, Record<string, string>>;
  gebruikers: GebruikerRij[];
  /** SPEC §19 */
  eigenVelden?: EigenVeld[];
}

export function useIk() {
  return useQuery({ queryKey: ['ik'], queryFn: () => api.get<Ik>('/api/ik'), retry: false, staleTime: 60000 });
}

/** SPEC §12.1: staat een module aan? */
export function useModule(m: Module) {
  const ik = useIk().data;
  return !!ik?.licentie?.modules.includes(m);
}

export function useMag() {
  const ik = useIk().data;
  return (r: Recht) => mag(ik?.rol, r);
}

/** Alle gegevens + rekencontext (zoals de collecties in de Power App). */
export function useData() {
  const q = useQuery({ queryKey: ['data'], queryFn: () => api.get<Data>('/api/data'), staleTime: 30000, refetchOnWindowFocus: true });
  const kal = useMemo(() => {
    if (!q.data) return null;
    const bezet: BezetMap = new Map(Object.entries(q.data.bezet).map(([k, v]) => [k, new Map(Object.entries(v))]));
    return new Kalender({
      engineers: q.data.engineers,
      taken: q.data.taken,
      afwezigheden: q.data.afwezigheden,
      feestdagen: q.data.feestdagen,
      bezet,
      instellingen: q.data.instellingen,
    });
  }, [q.data]);
  return { ...q, kal };
}

/** Voert een API-actie uit, toont een melding en ververst de gegevens. */
export function useActie<A = void, R = unknown>(fn: (a: A) => Promise<R>, opts: { succes?: string | ((r: R) => string | undefined); opNa?: (r: R) => void } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['data'] });
      const m = typeof opts.succes === 'function' ? opts.succes(r) : opts.succes;
      if (m) toast(m, 'succes');
      opts.opNa?.(r);
    },
    onError: (e: Error) => toast(e.message, 'fout'),
  });
}

export function projectVan(d: Data | undefined, id?: string | null) {
  return id ? d?.projecten.find((p) => p.id === id) : undefined;
}
export function engineerVan(d: Data | undefined, id?: string | null) {
  return id ? d?.engineers.find((e) => e.id === id) : undefined;
}

/** Bestemming van een taak voor de reistijd: afwijkend adres, anders de projectlocatie. */
export function bestemmingVan(d: Data | undefined) {
  return (t: Taak) => {
    if (t.afwAdres && t.afwLat != null) return { lat: t.afwLat, lon: t.afwLon, klant: t.klant };
    const p = projectVan(d, t.projectId);
    return p ? { lat: p.lat, lon: p.lon, klant: p.klant } : null;
  };
}

export const PROJECT_STATUS: Record<string, string> = {
  OFFERTE: 'Offerte',
  OPDRACHT: 'Opdracht',
  GEPLAND: 'Gepland',
  IN_UITVOERING: 'In uitvoering',
  ON_HOLD: 'On hold',
  AFGEROND: 'Afgerond',
};
export const AFW_SOORT: Record<string, string> = { VERLOF: 'Verlof', ZIEK: 'Ziek', CURSUS: 'Cursus / training', OVERIG: 'Overig' };
export const NIVEAUS = [
  { p: 100, label: '100% – specialist' },
  { p: 75, label: '75% – goede kennis' },
  { p: 50, label: '50% – redelijke kennis' },
  { p: 25, label: '25% – beperkte kennis' },
];

/** SPEC §4.6: taak openen als popup boven het huidige scherm. */
export function useTaakOpenen() {
  const nav = useNavigate();
  const loc = useLocation();
  const huidig = (loc.state as { achtergrond?: typeof loc } | null)?.achtergrond ?? loc;
  return {
    open: (pad: string) => nav(pad, { state: { achtergrond: huidig } }),
    state: { achtergrond: huidig },
  };
}
